// FlixDetailReaction.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import getCanShare from '../resource/getCanShare.js';
import Loading from '../component/Loading.jsx';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import TextEllipsis from '../style/TextEllipsis.js';
import { textColor, scrollBarHiddenPadding } from '../style/variables.js';
import Shake from '../style/Shake.js';

import UserAvatarWithStatus from '../container/UserAvatarWithStatus.js';
import UserStatusIndicator from '../container/UserStatusIndicator.js';
import NeedAuth from '../container/NeedAuth.js';
import WithPinMessageButton from '../container/WithPinMessageButton.js';
import WithMessagePinData from '../container/WithMessagePinData.js';
import WithClipMessage from '../container/WithClipMessage.js';

import useIsClient from '../hook/useIsClient.js';
import useShortShareLink, { ShareType } from '../hook/useShortShareLink.js';

import { getIsChinaPartner } from '../resource/partner.js';
import { ProfileListType } from '../resource/profileConstants.js';

import ChatIconResource from '../../img/btn_chat_24.svg';
import CopyIconResource from '../../img/ic_album.svg';
import ShareIconResource from '../../img/ic-share-20.svg';
import LikeIconResource from '../../img/btn_rating_like.svg';
import DislikeIconResource from '../../img/btn_rating_unlike.svg';
import ActiveLikeIconResource from '../../img/btn_rating_like_active.svg';
import ActiveDislikeIconResource from '../../img/btn_rating_unlike_active.svg';
import GiftIconResource from '../../img/ic_gift_fill.svg';

import trackMixpanel, {
  EventTypes,
  ButtonId,
  getViewId,
} from '../resource/mixpanel.js';

const isChinaPartner = getIsChinaPartner();
const canShare = getCanShare();

export const FlixDetailReaction = ({
  meId,
  messageId,
  senderId,
  senderUsername,
  unlockPrice,
  ratedAs,
  likeCount,
  isOnFreezoneDetail,
  isFollowing,
  shouldShowUser,
  isClipEnabled,
  isClipPermissionMatched,
  login,
  sendChatStart,
  submitMessageRating,
  setGiftPanel,
  sendFollowUser,
  copyTextWithToastr,
}) => {
  const { t } = useTranslation();
  const isSender = meId && meId === senderId;
  const isClient = useIsClient();
  const shouldHideChat = true;
  const handleShare = shortShareLink => {
    navigator.share({
      title: 'SWAG',
      text: isOnFreezoneDetail
        ? t('title_share_free_flix_link')
        : t('title_share_flix_link', { username: senderUsername }),
      url: shortShareLink,
    });
  };
  const [isFetchingShareLink, onClickShare] = useShortShareLink({
    targetId: messageId,
    shareType: ShareType.POST,
    onShortShareLinkFetched: handleShare,
  });
  const handleCopy = shortShareLink => {
    copyTextWithToastr({
      copiedText: shortShareLink,
      successfulText: t('copy_to_clipboard_successfully'),
      failedText: t('copy_to_clipboard_failed'),
    });
  };
  const [isFetchingCopyLink, onClickCopy] = useShortShareLink({
    targetId: messageId,
    shareType: ShareType.POST,
    onShortShareLinkFetched: handleCopy,
  });

  const trackUserClick = ({ buttonId }) => {
    trackMixpanel({
      type: EventTypes.USER_CLICKED,
      payload: {
        viewId: getViewId({ pathname: window.location.pathname }),
        buttonId,
        abData: {
          unlockPrice,
        },
      },
    });
  };
  const handleClickRating = ({ isLike }) => {
    trackUserClick({
      buttonId: isLike ? ButtonId.All.ButtonLike : ButtonId.All.ButtonDislike,
    });
    return submitMessageRating({ rating: isLike ? 100 : 0 });
  };
  const handleClickFollow = () => {
    trackUserClick({ buttonId: ButtonId.All.ButtonFollow });
    return sendFollowUser({ id: senderId });
  };
  const handleClickChat = () => {
    trackUserClick({ buttonId: ButtonId.All.ButtonChat });
    return sendChatStart({ userId: senderId });
  };
  const handleClickGift = () => {
    trackUserClick({ buttonId: ButtonId.All.ButtonGift });
    setGiftPanel({ isOpen: true });
    return sendChatStart({ userId: senderId });
  };

  const renderActionButton = ({ element, mixpanelButtonId }) => {
    return (
      <NeedAuth>
        {React.cloneElement(element, {
          'data-key': 'needAuth',
          onClick: () => {
            trackUserClick({ buttonId: mixpanelButtonId });
            return login();
          },
        })}
        {React.cloneElement(element, {
          'data-key': 'authing',
          onClick: () => null,
        })}
        {React.cloneElement(element, {
          'data-key': 'authed',
        })}
      </NeedAuth>
    );
  };

  return (
    <StyledFlixDetailReaction>
      <ActionButtonSection>
        <ActionButtonsScrollerWrapper>
          <ActionButtonsWrapper>
            <ActionButtons>
              {renderActionButton({
                mixpanelButtonId: ButtonId.All.ButtonLike,
                element: (
                  <ActionButton
                    onClick={() => handleClickRating({ isLike: true })}
                  >
                    <ActionIcon
                      src={
                        ratedAs === 100
                          ? ActiveLikeIconResource
                          : LikeIconResource
                      }
                      alt="Like"
                    />
                    {likeCount ? likeCount : t('general_like')}
                  </ActionButton>
                ),
              })}
              {renderActionButton({
                mixpanelButtonId: ButtonId.All.ButtonDislike,
                element: (
                  <ActionButton
                    onClick={() => handleClickRating({ isLike: false })}
                  >
                    <ActionIcon
                      src={
                        ratedAs === 0
                          ? ActiveDislikeIconResource
                          : DislikeIconResource
                      }
                      alt="Dislike"
                    />
                    {t('general_dislike')}
                  </ActionButton>
                ),
              })}
              {!isSender &&
                renderActionButton({
                  mixpanelButtonId: ButtonId.All.ButtonChat,
                  element: (
                    <ActionButton onClick={handleClickChat}>
                      <ActionIcon src={ChatIconResource} alt="Chat" />
                      {t('tabbar_chat')}
                    </ActionButton>
                  ),
                })}
              {!isSender &&
                renderActionButton({
                  mixpanelButtonId: ButtonId.All.ButtonGift,
                  element: (
                    <ActionButton onClick={handleClickGift}>
                      <ActionIcon src={GiftIconResource} alt="Gift" />
                      {t('gift_title')}
                    </ActionButton>
                  ),
                })}
              {isSender &&
                renderActionButton({
                  mixpanelButtonId: ButtonId.All.ButtonPin,
                  element: (
                    <WithMessagePinData
                      messageId={messageId}
                      profileListType={ProfileListType.FLIX}
                    >
                      {({ isPinned, iconSource }) => (
                        <WithPinMessageButton
                          messageId={messageId}
                          profileListType={ProfileListType.FLIX}
                          isPinned={isPinned}
                        >
                          {({ handleClick }) => (
                            <ActionButton onClick={handleClick}>
                              <ActionIcon
                                src={iconSource}
                                alt={t(
                                  isPinned ? 'general_pinned' : 'general_pin'
                                )}
                              />
                              {t(isPinned ? 'general_pinned' : 'general_pin')}
                            </ActionButton>
                          )}
                        </WithPinMessageButton>
                      )}
                    </WithMessagePinData>
                  ),
                })}
              {((isClipEnabled && isSender) || isClipPermissionMatched) &&
                renderActionButton({
                  mixpanelButtonId: ButtonId.All.ButtonVideoClip,
                  element: (
                    <WithClipMessage messageId={messageId}>
                      {({ iconSource, handleClick }) => (
                        <ActionButton onClick={handleClick}>
                          <ActionIcon
                            src={iconSource}
                            alt={t('general_clip')}
                          />
                          {t('general_clip')}
                        </ActionButton>
                      )}
                    </WithClipMessage>
                  ),
                })}
              {isClient && canShare && !isChinaPartner && (
                <ActionButton
                  onClick={onClickShare}
                  disabled={isFetchingShareLink}
                >
                  {isFetchingShareLink ? (
                    <Loading />
                  ) : (
                    <ActionIcon src={ShareIconResource} alt="Share" />
                  )}
                  {t('general_share')}
                </ActionButton>
              )}
              {!isChinaPartner && !canShare && (
                <ActionButton
                  onClick={onClickCopy}
                  disabled={isFetchingCopyLink}
                >
                  {isFetchingCopyLink ? (
                    <Loading />
                  ) : (
                    <ActionIcon src={CopyIconResource} alt="Copy" />
                  )}
                  {t('general_copy')}
                </ActionButton>
              )}
            </ActionButtons>
          </ActionButtonsWrapper>
        </ActionButtonsScrollerWrapper>
      </ActionButtonSection>
      {shouldShowUser && (
        <>
          <Divider />
          <UserSection shouldHideChat={shouldHideChat}>
            <AvatarWrapper>
              <UserAvatarWithStatus id={senderId} size={28} circleOffset={2} />
            </AvatarWrapper>
            <UsernameWrapper>
              <Username to={`/user/${senderId}/flix`}>
                {senderUsername ? `@${senderUsername}` : '-'}
              </Username>
              <UserStatusWrapper>
                <UserStatusIndicator
                  userId={senderId}
                  shouldShowLiveStream
                  shouldShowOnline
                />
              </UserStatusWrapper>
              {!isFollowing && (
                <FollowButton onClick={handleClickFollow}>
                  {t('follow')}
                </FollowButton>
              )}
            </UsernameWrapper>
            {!shouldHideChat && (
              <ChatButtonWrapper>
                <ChatButton onClick={handleClickChat}>
                  <ChatIcon src={ChatIconResource} />
                  {t('chat_with_me')}
                </ChatButton>
                <GiftButton onClick={handleClickGift}>
                  <GiftIcon src={GiftIconResource} alt="Gift" />
                </GiftButton>
              </ChatButtonWrapper>
            )}
          </UserSection>
        </>
      )}
    </StyledFlixDetailReaction>
  );
};

FlixDetailReaction.propTypes = {
  meId: PropTypes.string,
  messageId: PropTypes.string,
  senderId: PropTypes.string,
  senderUsername: PropTypes.string,
  unlockPrice: PropTypes.number,
  ratedAs: PropTypes.number,
  likeCount: PropTypes.number,
  isOnFreezoneDetail: PropTypes.bool,
  isFollowing: PropTypes.bool,
  shouldShowUser: PropTypes.bool,
  isClipEnabled: PropTypes.bool,
  isClipPermissionMatched: PropTypes.bool,
  login: PropTypes.func,
  sendChatStart: PropTypes.func,
  submitMessageRating: PropTypes.func,
  setGiftPanel: PropTypes.func,
  sendFollowUser: PropTypes.func,
  copyTextWithToastr: PropTypes.func,
};

FlixDetailReaction.defaultProps = {
  meId: null,
  senderId: null,
  senderUsername: '',
  messageId: '',
  unlockPrice: null,
  ratedAs: null,
  likeCount: 0,
  isOnFreezoneDetail: false,
  isFollowing: false,
  shouldShowUser: false,
  isClipEnabled: false,
  isClipPermissionMatched: false,
  login: () => null,
  sendChatStart: () => null,
  submitMessageRating: () => null,
  setGiftPanel: () => null,
  sendFollowUser: () => null,
  copyTextWithToastr: () => null,
};

const StyledFlixDetailReaction = styled.div`
  width: inherit;
`;

const ActionButtonSection = styled.div`
  height: 64px;
  overflow-y: hidden;
`;

const ActionButtonsScrollerWrapper = styled.div`
  overflow-y: hidden;
  overflow-x: auto;
`;

const ActionButtonsWrapper = styled.div`
  padding-bottom: ${scrollBarHiddenPadding}px;
  display: inline-block;
`;

const ActionButtons = styled.div`
  display: inline-flex;
  flex-wrap: nowrap;
`;

const ActionButton = styled.button`
  ${ResetButtonStyle};
  border-radius: 50%;
  margin-right: 12px;
  width: 64px;
  height: 64px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${textColor.white};
  font-size: 12px;
  line-height: 1;
  transition: background-color 0.2s;
  :last-child {
    margin-right: 0px;
  }
  :active {
    background-color: rgba(100, 100, 100, 0.2);
  }
`;

const ActionIcon = styled.img`
  margin-bottom: 8px;
  width: 32px;
  height: 32px;
`;

const ChatIcon = styled.img`
  width: 24px;
  height: 24px;
  margin-right: 4px;
`;

const Divider = styled.div`
  margin-top: 12px;
  margin-bottom: 12px;
  height: 1px;
  background-color: rgba(100, 100, 100, 0.15);
`;

const UserSection = styled.div`
  padding-left: 16px;
  padding-right: 16px;
  display: grid;
  ${({ shouldHideChat }) => {
    if (shouldHideChat) {
      return css`
        grid-template-areas: 'avatar username';
      `;
    }
    return css`
      grid-template-areas:
        'avatar username'
        'avatar chat-button';
    `;
  }}
  grid-template-columns: auto 1fr;
  grid-column-gap: 12px;
  grid-row-gap: 12px;
`;

const AvatarWrapper = styled.div`
  grid-area: avatar;
`;

const UsernameWrapper = styled.div`
  grid-area: username;
  display: flex;
  align-items: center;
  overflow: hidden;
  color: ${textColor.white};
  font-size: 14px;
`;

const Username = styled(Link)`
  ${TextEllipsis};
`;

const UserStatusWrapper = styled.div`
  margin-left: 4px;
`;

const FollowButton = styled.button`
  ${ResetButtonStyle};
  flex: none;
  color: ${textColor.primary};
  font-size: 14px;
  ::before {
    content: '・';
    padding: 0px 4px;
    color: ${textColor.white};
    font-size: 14px;
    font-weight: 600;
  }
`;

const ChatButtonWrapper = styled.div`
  grid-area: chat-button;
  display: flex;
`;

const ChatButton = styled.button`
  ${ResetButtonStyle};
  border-radius: 18px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: auto;
  height: 36px;
  color: ${textColor.white};
  font-size: 14px;
  font-weight: 600;
  background-image: linear-gradient(
    103deg,
    #5d82bc,
    #00d2be,
    #5d82bc,
    #8e37d7,
    #5d82bc,
    #00d2be,
    #5d82bc
  );
  background-size: 300% 100%;
  @keyframes marquee {
    0% {
      background-position: 100% 0;
    }
    100% {
      background-position: 0 0;
    }
  }
  animation: 1.4s infinite linear marquee;
`;

const GiftButton = styled.button`
  ${ResetButtonStyle};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background-image: linear-gradient(
    135deg,
    #00d2be,
    rgb(93, 130, 188) 52%,
    #8e37d7
  );
`;

const GiftIcon = styled.img`
  height: 24px;
  width: auto;
  animation: 1.4s infinite linear ${Shake};
`;

export default FlixDetailReaction;
